import {
  AutomationScheduleMode,
  AutomationScheduleRelativeDirection,
  AutomationStatus,
  AutomationTriggerType,
  AutomationType,
} from "../../config/enums";
import {
  validateSendWindowEnd,
  validateSendWindowStart,
} from "../communication/sendWindow";
import { FieldBehaviour } from "../../config/fieldBehaviours";
import { FieldValidator } from "../../config/fieldValidators";
import { VisibilityRule } from "../../config/visibilityRules";

type Rec = Record<string, unknown>;

const is = (value: unknown, expected: string | number) =>
  String(value ?? "") === String(expected);

export const isScheduled = (record: Rec) =>
  is(record.type, AutomationType.Scheduled);

export const isTriggered = (record: Rec) =>
  is(record.type, AutomationType.Triggered);

// A new triggered automation has no trigger type until one is chosen, which is Abandoned Basket.
const isAbandonedBasketTrigger = (record: Rec) =>
  isTriggered(record) &&
  is(record.triggertype || AutomationTriggerType["Abandoned Basket"], AutomationTriggerType["Abandoned Basket"]);

// "FixedTime" and "FixtureRelative" are what older records hold for these two modes.
const isDailyMode = (mode: unknown) =>
  mode === AutomationScheduleMode.Daily || mode === "FixedTime";

export const isFixtureMode = (mode: unknown) =>
  mode === AutomationScheduleMode.Fixture || mode === "FixtureRelative";

const hasScheduleMode = (record: Rec) =>
  isScheduled(record) && Boolean(record.schedulemode);

const isFixtureSchedule = (record: Rec) =>
  isScheduled(record) && isFixtureMode(record.schedulemode);

const isSameDay = (record: Rec) =>
  is(
    record.fixturerelativedirection,
    AutomationScheduleRelativeDirection["Same Day"]
  );

const hasFixtureDirection = (record: Rec) =>
  isFixtureSchedule(record) && Boolean(record.fixturerelativedirection);

/** Published and Testing automations can create executions, so they are read-only. */
export const isActiveAutomation = (record: Rec) =>
  is(record.status, AutomationStatus.Published) ||
  is(record.status, AutomationStatus.Testing);

export const automationVisibilityRules: Record<string, VisibilityRule> = {
  automationIsScheduled: isScheduled,
  automationIsTriggered: isTriggered,
  automationIsAbandonedBasket: isAbandonedBasketTrigger,
  automationHasDelivery: (record) =>
    hasScheduleMode(record) || isTriggered(record),
  automationHasScheduleMode: hasScheduleMode,
  automationIsDaily: (record) =>
    isScheduled(record) && isDailyMode(record.schedulemode),
  automationIsFixture: isFixtureSchedule,
  automationHasFixtureDirection: hasFixtureDirection,
  automationNeedsFixtureOffset: (record) =>
    hasFixtureDirection(record) && !isSameDay(record),
  automationSaved: (record) => Boolean(record.id),
  automationNew: (record) => !record.id,
  automationSavedScheduled: (record) =>
    Boolean(record.id) && hasScheduleMode(record),
  // Delivery is the form, whatever the type, until the automation is published, then a read-only card.
  automationEditableDelivery: (record) => !isActiveAutomation(record),
  automationActiveDelivery: (record) =>
    isActiveAutomation(record) && (hasScheduleMode(record) || isTriggered(record)),
  automationEditable: (record) => !isActiveAutomation(record),
};

const wholeNumberUpTo = (maximum: number, value: unknown) => {
  const number = Number(value);

  return Number.isInteger(number) && number >= 1 && number <= maximum
    ? ""
    : `Enter a whole number from 1 to ${maximum}`;
};

export const automationValidators: Record<string, FieldValidator> = {
  // A searchable lookup has no native control for the browser's required check to read.
  automationRequiredLookup: (value) =>
    value === null || value === undefined || value === "" ? "This field is required" : "",
  automationType: (value) =>
    value === null || value === undefined || value === ""
      ? "Choose an automation type"
      : "",
  automationScheduleMode: (value) =>
    value === null || value === undefined || value === ""
      ? "Choose a schedule type"
      : "",
  automationFixtureOffset: (value, record) =>
    record.fixturerelativedirection && !isSameDay(record)
      ? wholeNumberUpTo(100, value)
      : "",
  automationTriggerInterval: (value) => wholeNumberUpTo(365, value),
  automationSendWindowStart: (value, record) =>
    isAbandonedBasketTrigger(record)
      ? validateSendWindowStart(value, record, "sendwindowend")
      : "",
  automationSendWindowEnd: (value, record) =>
    isAbandonedBasketTrigger(record)
      ? validateSendWindowEnd(value, record, "sendwindowstart")
      : "",
};

/** A same-day fixture schedule has no offset, so choosing it clears the one entered. */
export const automationBehaviours: Record<string, FieldBehaviour> = {
  automationClearOffsetForSameDay: (value, record) =>
    is(value, AutomationScheduleRelativeDirection["Same Day"])
      ? ""
      : record.fixturerelativeoffset,
};
